import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchResources } from '@/mocks/resources';

export default function HeroSearch() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<{ id: string; title: string; category: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInput = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      debounceRef.current = setTimeout(() => {
        const results = searchResources(value.trim());
        setSuggestions(
          results.slice(0, 6).map((r) => ({ id: r.id, title: r.title, category: r.category }))
        );
        setShowSuggestions(results.length > 0);
      }, 200);
    },
    []
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    if (query.trim()) {
      navigate(`/category/all?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const hotSearches = ['流浪地球2', '周杰伦', 'Adobe', '三体', '艾尔登法环', 'Python教程'];

  return (
    <section className="relative w-full min-h-[600px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20geometric%20technology%20background%20with%20flowing%20gradient%20waves%20in%20warm%20orange%20and%20deep%20charcoal%20tones%2C%20subtle%20circuit%20board%20patterns%2C%20modern%20tech%20aesthetic%2C%20clean%20minimal%20design%2C%20smooth%20gradients%2C%20digital%20atmosphere%2C%20no%20text%2C%20no%20logos&width=1600&height=900&seq=hero-bg-2026&orientation=landscape"
          alt="磁力猫搜索背景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/45 to-black/65"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-extrabold text-background-50 mb-4 leading-tight">
          海量资源，一搜即达
        </h1>
        <p className="text-base md:text-lg text-background-50/80 mb-10 max-w-2xl mx-auto leading-relaxed">
          磁力猫是专业的磁力链接搜索引擎，提供海量影视、音乐、软件、电子书等资源的磁力链接免费搜索与下载服务
        </p>

        <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
          <div className="relative">
            <div className="flex items-center bg-background-50 rounded-2xl overflow-hidden">
              <span className="pl-5 text-foreground-400">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => handleInput(e.target.value)}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                placeholder="输入关键词搜索资源..."
                className="flex-1 py-4 px-3 text-sm text-foreground-950 bg-transparent outline-none placeholder:text-foreground-300"
              />
              <button
                type="submit"
                className="mr-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>

            {showSuggestions && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowSuggestions(false)}></div>
                <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl border border-background-200/80 overflow-hidden z-20">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setQuery(s.title);
                        setShowSuggestions(false);
                        navigate(`/category/all?q=${encodeURIComponent(s.title)}`);
                      }}
                      className="w-full flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors text-left cursor-pointer"
                    >
                      <i className="ri-search-line text-foreground-400"></i>
                      <span>{s.title}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <span className="text-xs text-background-50/60">热门搜索：</span>
            {hotSearches.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => {
                  setQuery(term);
                  navigate(`/category/all?q=${encodeURIComponent(term)}`);
                }}
                className="px-3 py-1 rounded-full border border-background-50/30 text-xs text-background-50/80 hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
              >
                {term}
              </button>
            ))}
          </div>
        </form>

        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mt-12">
          {[
            { value: '50万+', label: '资源总量' },
            { value: '99.9%', label: '搜索成功率' },
            { value: '100万+', label: '日活用户' },
            { value: '8大', label: '资源分类' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-xl md:text-2xl font-heading font-bold text-background-50">{stat.value}</div>
              <div className="text-xs text-background-50/60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}